import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Popconfirm, Card, Input, Button, Switch, Select, Space, Typography, Upload, Tabs, message, Tag, Alert } from 'antd';
import { DeleteOutlined, FileOutlined, InboxOutlined, EditOutlined, FileTextOutlined, MailOutlined } from '@ant-design/icons';
import NodeInputHandler from '../common/NodeInputHandler';
import GenericNodeContent from '../common/GenericNodeContent';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const { TabPane } = Tabs;
const { Text } = Typography;
const { Dragger } = Upload;


const SendEmailNode = ({ id, data, isConnectable, nodes }) => {
  const [nodeName, setNodeName] = useState(data.nodeName || `Email ${id}`);
  const [to, setTo] = useState(data.to || '');
  const [cc, setCc] = useState(data.cc || '');
  const [bcc, setBcc] = useState(data.bcc || '');
  const [subject, setSubject] = useState(data.subject || '');
  const [body, setBody] = useState(data.body || '');
  const [useHtml, setUseHtml] = useState(data.useHtml || true);
  const [attachments, setAttachments] = useState(data.attachments || []);
  const [delayEnabled, setDelayEnabled] = useState(data.delayEnabled || false);
  const [delayValue, setDelayValue] = useState(data.delayValue || 1);
  const [delayUnit, setDelayUnit] = useState(data.delayUnit || 'minutes');

  useEffect(() => {
    data.updateNodeData(id, {
      nodeName,
      to,
      cc,
      bcc,
      subject,
      body,
      useHtml,
      attachments,
      delayEnabled,
      delayValue,
      delayUnit
    });
  }, [
    nodeName, to, cc, bcc, subject, body, useHtml,
    attachments, delayEnabled, delayValue, delayUnit
  ]);

  const handleAttachmentUpload = async (info) => {
    const { status, response } = info.file;
    if (status === 'done') {
      addAttachment({
        id: response.id,
        name: info.file.name,
        url: response.url,
        source: 'upload'
      });
      message.success(`${info.file.name} uploaded successfully`);
    } else if (status === 'error') {
      message.error(`${info.file.name} upload failed`);
    }
  };

  const addAttachment = (attachment) => {
    if (!attachments.some(a => a.url === attachment.url)) {
      setAttachments(prev => [...prev, attachment]);
    } else {
      message.warning('This attachment has already been added');
    }
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(attachments.filter(att => att.id !== attachmentId));
  };

  const uploadProps = {
    name: 'file',
    multiple: true,
    action: '/wp-json/wp-ai-workflows/v1/upload-attachment',
    onChange: handleAttachmentUpload,
    headers: {
      'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce
    },
    showUploadList: false,
    accept: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.zip,.rar,.jpg,.jpeg,.png,.gif,.html'
  };

  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      ['link'],
      [{ 'color': [] }],
      ['clean']
    ]
  };

  const quillStyles = {
    container: {
      '& .ql-container.ql-snow': {
        borderBottom: 'none',
        borderLeft: 'none',
        borderRight: 'none'
      },
      '& .ql-toolbar.ql-snow': {
        border: 'none',
        borderBottom: '1px solid #d9d9d9'
      },
      '& .ql-editor': {
        minHeight: '150px',
        maxHeight: '300px',
        overflow: 'auto'
      }
    }
  };

  const renderAttachmentsList = () => (
    <div style={{ marginTop: 16 }}>
      {attachments.map(attachment => (
        <div 
          key={attachment.id} 
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            padding: '8px',
            background: '#f5f5f5',
            borderRadius: '4px',
            marginBottom: '8px'
          }}
        >
          <Space>
            <FileOutlined />
            <span>{attachment.name}</span>
            <Tag color={
              attachment.source === 'external' ? 'blue' : 
              attachment.source === 'node' ? 'purple' :
              'green'
            }>
              {attachment.source}
            </Tag>
          </Space>
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />}
            onClick={() => removeAttachment(attachment.id)}
          />
        </div>
      ))}
    </div>
  );

  return (
    <Card 
      title="Send Email"
      extra={
        <Space>
          <Switch
            checkedChildren="HTML"
            unCheckedChildren="Text"
            checked={useHtml}
            onChange={setUseHtml}
          />
          <Popconfirm
            title="Delete Node"
            description="Are you sure you want to delete this node?"
            onConfirm={() => data.onDelete(id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          > 
            <Button icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      }
      style={{ width: 400 }}
      bodyStyle={{ padding: '15px' }}
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      
      <Input
        placeholder="Node Name"
        value={nodeName}
        onChange={(e) => setNodeName(e.target.value)}
        style={{ marginBottom: 8 }}
        className="nodrag"
      />

      <Tabs defaultActiveKey="1">
        <TabPane tab="Email" key="1">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input
              addonBefore="To:"
              placeholder="Primary recipients"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="nodrag"
            />
            <NodeInputHandler nodeId={id} onInsert={(tag) => setTo(prev => prev + tag)} />

            <Input
              addonBefore="CC:"
              placeholder="Carbon copy recipients"
              value={cc}
              onChange={(e) => setCc(e.target.value)}
              className="nodrag"
            />
            <NodeInputHandler nodeId={id} onInsert={(tag) => setCc(prev => prev + tag)} />

            <Input
              addonBefore="BCC:"
              placeholder="Blind carbon copy recipients"
              value={bcc}
              onChange={(e) => setBcc(e.target.value)}
              className="nodrag"
            />
            <NodeInputHandler nodeId={id} onInsert={(tag) => setBcc(prev => prev + tag)} />

            <Input
              placeholder="Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="nodrag"
            />
            <NodeInputHandler nodeId={id} onInsert={(tag) => setSubject(prev => prev + tag)} />

            {useHtml ? (
              <div className="nodrag">
                <div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', marginBottom: 8 }}>
                  <ReactQuill 
                    value={body} 
                    onChange={setBody}
                    modules={quillModules}
                    style={{ 
                      backgroundColor: 'white',
                      border: 'none'
                    }}
                  />
                </div>
                <NodeInputHandler nodeId={id} onInsert={(tag) => setBody(prev => prev + tag)} />
              </div>
            ) : (
              <div className="nodrag">
                <GenericNodeContent
                  id={id}
                  data={{ content: body }}
                  onChange={(_, newData) => setBody(newData.content)}
                  placeholder="Email body"
                  nodes={nodes}
                  style={{ minHeight: '100px' }}
                />
              </div>
            )}
          </Space>
        </TabPane>

        <TabPane tab="Attachments" key="2">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Alert
              message="Supported File Types"
              description={
                <Text type="secondary">
                  Documents: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, CSV
                  <br />
                  Archives: ZIP, RAR
                  <br />
                  Images: JPG, PNG, GIF
                </Text>
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            {/* URL/Node Input Section */}
            <div style={{ marginBottom: 16 }}>
              <Text strong>Add From URL or Previous Node</Text>
              <GenericNodeContent
                id={id}
                data={{ content: '' }}
                onChange={(_, newData) => {
                  if (newData.content) {
                    addAttachment({
                      id: `ext-${Date.now()}`,
                      name: newData.content.split('/').pop() || 'attachment',
                      url: newData.content,
                      source: newData.content.includes('[Input from') ? 'node' : 'external'
                    });
                  }
                }}
                placeholder="Enter URL or select from previous nodes"
                nodes={nodes}
              />
            </div>

            {/* File Upload Section */}
            <Text strong>Upload Files</Text>
            <Dragger {...uploadProps} className="nodrag">
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">Click or drag files to upload</p>
            </Dragger>

            {/* Attachments List */}
            {attachments.length > 0 && renderAttachmentsList()}
          </Space>
        </TabPane>

        <TabPane tab="Scheduling" key="3">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Switch
              checkedChildren="Delay Enabled"
              unCheckedChildren="Send Immediately"
              checked={delayEnabled}
              onChange={setDelayEnabled}
            />
            {delayEnabled && (
              <Space>
                <Input
                  type="number"
                  value={delayValue}
                  onChange={(e) => setDelayValue(e.target.value)}
                  style={{ width: 100 }}
                  className="nodrag"
                />
                <Select
                  value={delayUnit}
                  onChange={setDelayUnit}
                  className="nodrag"
                >
                  <Select.Option value="minutes">Minutes</Select.Option>
                  <Select.Option value="hours">Hours</Select.Option>
                  <Select.Option value="days">Days</Select.Option>
                </Select>
              </Space>
            )}
          </Space>
        </TabPane>
      </Tabs>

      <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
      <style jsx>{`
        .ql-container.ql-snow {
          border: none;
        }
        .ql-toolbar.ql-snow {
          border: none;
          border-bottom: 1px solid #d9d9d9;
        }
        .ql-editor {
          min-height: 150px;
          max-height: 300px;
          overflow: auto;
        }
      `}</style>
    </Card>
  );
};

export default SendEmailNode;